import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

type ForgotForm = { email: string };

const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotForm>();

  const onSubmit = async (data: ForgotForm) => {
    setIsLoading(true);
    try {
      const res = await authAPI.forgotPassword({ email: data.email.trim() });
      toast.success(res.data?.message || 'Revisa tu correo.');
    } catch {
      toast.error('No se pudo enviar la solicitud. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-10 lg:p-12">
          <div className="mb-10">
            <h2 className="text-3xl font-light text-slate-800 tracking-tight mb-2">
              Recuperar contraseña
            </h2>
            <p className="text-sm text-slate-500 font-light">
              Indica tu correo y te enviaremos un enlace para restablecerla.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Correo electrónico
              </label>
              <div className="relative group">
                <div
                  className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors duration-300 ${
                    emailFocused ? 'text-slate-600' : 'text-slate-400'
                  }`}
                >
                  <EnvelopeIcon className="h-5 w-5" />
                </div>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="correo@ejemplo.com"
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  className={`block w-full pl-12 pr-4 py-3.5 text-base bg-white border rounded-xl transition-all duration-300 placeholder-slate-400 focus:outline-none ${
                    errors.email
                      ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                      : emailFocused
                        ? 'border-slate-400 shadow-md shadow-slate-200/50 ring-2 ring-slate-200/50'
                        : 'border-slate-300 hover:border-slate-400'
                  }`}
                  {...register('email', {
                    required: 'El correo electrónico es requerido',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Correo electrónico inválido'
                    }
                  })}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center px-6 py-4 text-base font-medium text-white bg-slate-700 rounded-xl shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Enviando…' : 'Enviar enlace'}
              </button>
            </div>

            <div className="text-center pt-2">
              <Link
                to="/login"
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors duration-200 font-light"
              >
                Volver al inicio de sesión
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
